import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchLatestPosts } from "../services/firestore";
import "./Sidebar.css";

/* ── SVG category icons ── */
const PoliticsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const EntertainmentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
  </svg>
);

const SecurityIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l8 4v6c0 5.25-3.5 9.74-8 11-4.5-1.26-8-5.75-8-11V6l8-4z"/>
    <path d="M12 11v4"/>
    <path d="M10 13h4"/>
  </svg>
);

const EducationIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7l8-4 8 4-8 4-8-4z"/>
    <path d="M4 7v6c0 5 4 7 8 7s8-2 8-7V7"/>
    <path d="M12 3v18"/>
  </svg>
);

const SportsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    <path d="M2 12h20"/>
  </svg>
);

const BusinessIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const TechIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const HealthIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
  </svg>
);

const ViralIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const CATEGORIES = [
  { name: "Politics",      Icon: PoliticsIcon      },
  { name: "Entertainment", Icon: EntertainmentIcon },
  { name: "Sports",        Icon: SportsIcon        },
  { name: "Business",      Icon: BusinessIcon      },
  { name: "Technology",    Icon: TechIcon          },
  { name: "Security",      Icon: SecurityIcon      },
  { name: "Education",     Icon: EducationIcon     },
  { name: "Health",        Icon: HealthIcon        },
  { name: "Viral",         Icon: ViralIcon         },
];

function timeAgo(timestamp) {
  if (!timestamp) return "";
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function Sidebar() {
  const [recent, setRecent] = useState([]);

  useEffect(() => { fetchLatestPosts(5).then(setRecent); }, []);

  return (
    <aside className="sidebar">

      {/* ── Categories ── */}
      <div className="sidebar-widget">
        <h3 className="sidebar-widget-title">
          <span className="widget-title-bar" />
          Browse Categories
        </h3>
        <ul className="sidebar-categories">
          {CATEGORIES.map(({ name, Icon }) => (
            <li key={name}>
              <Link to={`/category/${name.toLowerCase()}`}>
                <span className="cat-icon"><Icon /></span>
                <span className="cat-name">{name}</span>
                <span className="cat-chevron"><ChevronIcon /></span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Recent Posts ── */}
      <div className="sidebar-widget">
        <h3 className="sidebar-widget-title">
          <span className="widget-title-bar" />
          Recent Posts
        </h3>
        <ul className="sidebar-recent">
          {recent.map((post) => (
            <li key={post.id} className="sidebar-recent-item">
              <Link to={`/post/${post.id}`}>
                <img src={post.imageUrl} alt={post.title} className="sidebar-recent-img" />
                <div className="sidebar-recent-info">
                  <p className="sidebar-recent-title">{post.title}</p>
                  <span className="sidebar-recent-time">
                    <ClockIcon /> {timeAgo(post.timestamp)}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Ad slot ── */}
      <div className="sidebar-widget sidebar-ad">
        <p className="sidebar-ad-label">Sponsored</p>
        <div className="sidebar-ad-placeholder">
          <span>Your Ad Here</span>
        </div>
      </div>

    </aside>
  );
}
